/**
 * Usage Analytics (P29 + P30 strip).
 *
 * Live data from `/api/v1/usage/rollups` (24h window). Wireframe
 * EnterpriseChart placeholders are now replaced with real recharts plots
 * driven by the rollup data.
 */
import type { JSX } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Activity, AlertTriangle, Clock3, Users } from 'lucide-react';

import { useUsageRollups } from '@/api/index.js';
import { MetricCard } from '@/components/console/MetricCard.js';
import { PageHeader } from '@/components/console/PageHeader.js';
import { SectionCard } from '@/components/console/SectionCard.js';
import { AsyncShell } from '@/components/console/StateBlocks.js';

const PIE_COLORS = { allow: '#5fb27a', deny: '#d97766', error: '#e0a85c', unknown: '#8a8a8a' };

export default function UsageAnalytics(): JSX.Element {
  const rollups = useUsageRollups(24);
  const data = rollups.data;
  const totalEvents24h = data
    ? data.decision_histogram.allow + data.decision_histogram.deny + data.decision_histogram.error
    : 0;
  const denied = data?.decision_histogram.deny ?? 0;
  const errorCount = data?.decision_histogram.error ?? 0;
  const distinctMethods = data?.by_method.length ?? 0;

  // Format hourly bucket data for the line chart
  const hourSeries = (data?.by_hour ?? []).map((row) => ({
    hour: new Date(row.ts).toLocaleTimeString([], { hour: '2-digit' }),
    count: row.count,
  }));
  const methodSeries = (data?.by_method ?? []).slice(0, 10);
  const pieData = data
    ? Object.entries(data.decision_histogram).map(([k, v]) => ({ name: k, value: v }))
    : [];

  return (
    <>
      <PageHeader
        eyebrow="Operate"
        title="Usage Analytics"
        description="Audit-event rollups over the last 24 hours. All numbers come from the audit_outbox table."
      />

      <AsyncShell
        isLoading={rollups.isLoading}
        isError={rollups.isError}
        isEmpty={!data}
        error={rollups.error}
        loadingTitle="Loading usage rollups"
        errorTitle="Usage rollups unreachable"
      >
        {data && (
          <>
            <div className="metric-grid">
              <MetricCard
                icon={Activity}
                label="Audit events (24h)"
                value={totalEvents24h.toLocaleString()}
                delta={`${distinctMethods} distinct methods`}
                tone="good"
              />
              <MetricCard
                icon={Clock3}
                label="Allowed"
                value={(data.decision_histogram.allow ?? 0).toLocaleString()}
                delta="decision: allow"
              />
              <MetricCard
                icon={AlertTriangle}
                label="Denied"
                value={denied.toLocaleString()}
                delta={`${errorCount} errors`}
                tone={denied > 0 ? 'warn' : 'good'}
              />
              <MetricCard
                icon={Users}
                label="Top method"
                value={data.by_method[0]?.method ?? '—'}
                delta={
                  data.by_method[0]
                    ? `${data.by_method[0].count.toLocaleString()} calls`
                    : 'No data yet'
                }
              />
            </div>

            <p className="subtle" style={{ fontSize: 14, marginBottom: 16 }}>
              Last <strong>{data.window_hours}h</strong>; total events all time:{' '}
              <strong>{data.total_events_all_time.toLocaleString()}</strong>.
            </p>

            <SectionCard
              icon={Activity}
              title={`Calls by method (top ${methodSeries.length})`}
              text="Most-frequently invoked methods in the window."
            >
              {methodSeries.length === 0 ? (
                <p className="subtle">No events in the last 24h.</p>
              ) : (
                <div style={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer>
                    <BarChart data={methodSeries}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis dataKey="method" tick={{ fontSize: 13 }} />
                      <YAxis tick={{ fontSize: 13 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#5fb27a" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </SectionCard>

            <SectionCard
              icon={Clock3}
              title="Volume over time"
              text="Audit events grouped by hour."
            >
              {hourSeries.length === 0 ? (
                <p className="subtle">No events in the last 24h.</p>
              ) : (
                <div style={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer>
                    <LineChart data={hourSeries}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis dataKey="hour" tick={{ fontSize: 13 }} />
                      <YAxis tick={{ fontSize: 13 }} />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke="#5fb27a"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </SectionCard>

            <SectionCard
              icon={AlertTriangle}
              title="Decision breakdown"
              text="Share of all decisions by outcome."
            >
              <div style={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={100}
                      label={(entry) => `${entry.name}: ${entry.value}`}
                    >
                      {pieData.map((entry) => (
                        <Cell
                          key={entry.name}
                          fill={
                            PIE_COLORS[entry.name as keyof typeof PIE_COLORS] ?? PIE_COLORS.unknown
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </SectionCard>
          </>
        )}
      </AsyncShell>
    </>
  );
}
